import {
    Dispatch,
    Fragment,
    SetStateAction,
    useEffect,
    useLayoutEffect,
    useState,
} from 'react'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/solid'
import { classNames } from '@progress/kendo-react-common'
import { Tooltip } from '@progress/kendo-react-tooltip'
import { DropPosition } from '../common'
import React from 'react'
import { getTranslation } from 'utils/language-helper'
import { useAppContext } from 'context/AppContext'
import Translation from 'components/Translation'

type LocationSelectorProps = {
    allow_multiple?: boolean
    className?: string
    position?: DropPosition
    items: Record<string, string>[]
    selected?: Record<string, string>
    onCompleteSelection?(selections: Record<string, string>[]): void
    setSelectedLocations?: Dispatch<SetStateAction<string[]>>
    selectedLocations?: string[]
    disable?: boolean
}
export function LocationSelector(props: LocationSelectorProps) {
    const { translations } = useAppContext()
    const title_label_ref = React.createRef<HTMLSpanElement>()
    const tooltip_ref = React.useRef<Tooltip>(null)
    const [is_title_label_overflowed, toggleTitleLabelOverflow] =
        useState<boolean>(false)
    const [is_opened, openMenu] = useState(false)
    const [is_all_selected, setAllSelected] = useState<boolean>(
        props?.selectedLocations?.length == props?.items?.length
    )
    const [selected_items, setSelection] = useState<number[]>([])
    const [title, setTitle] = useState<string>('')

    useEffect(() => {
        setAllSelected(
            props?.selectedLocations?.length == props?.items?.length
        )
    }, [props.selectedLocations?.length])

    function handleSelectedItem(idx: number, is_selected: boolean) {
        const selection_idx = selected_items.indexOf(idx)
        const selections = selected_items
        if (selection_idx >= 0) {
            selections.splice(selection_idx, 1)
        } else {
            selections.push(idx)
        }
        if (props.allow_multiple) {
            setSelection(selections)
        } else {
            setSelection([idx])
        }
    }

    useEffect(() => {
        if (!is_opened) {
            if (props.onCompleteSelection) {
                props.onCompleteSelection(
                    selected_items.map((selected_idx: number) => {
                        return props.items[selected_idx]
                    })
                )
            }
        }
    }, [is_opened, selected_items])

    useLayoutEffect(() => {
        if (
            title_label_ref?.current &&
            title_label_ref.current.clientWidth <
                title_label_ref.current.scrollWidth
        ) {
            toggleTitleLabelOverflow(true)
        } else {
            toggleTitleLabelOverflow(false)
        }
    }, [title_label_ref])

    const selectLocationLandler = (location: any) => {
        localStorage.setItem('LOCATION_LOCAL', JSON.stringify(location))

        props.setSelectedLocations &&
            props?.setSelectedLocations((prev) => {
                const temp = []
                temp.push(location?.id || '')
                return temp
            })
        setTitle(location.text)
        setAllSelected(false)
    }

    const selectAllHandler = () => {
        localStorage.removeItem('LOCATION_LOCAL')
        props.setSelectedLocations &&
            props.setSelectedLocations(props?.items?.map((item) => item.id))
        setAllSelected(true)
    }

    const getValue = (idx: number) => {
        return (
            props.items.findIndex((itm) =>
                props.selectedLocations?.includes(itm.id)
            ) === idx
        )
    }

    const getTitle = () => {
        return is_all_selected
            ? getTranslation("all_locations", translations)
            : title ||
                  props.items.filter((item) =>
                      props.selectedLocations?.some((i) => i === item.id)
                  )[0]?.text
    }

    return (
        <Menu
            as="div"
            className={classNames(
                'relative inline-block text-left',
                props.className
            )}
        >
            {({ open }) => (
                <>
                    <Menu.Button
                        disabled={props.disable || false}
                        className={classNames(
                            'inline-flex justify-center items-center w-full',
                            'rounded-md border shadow-sm p-2 bg-white',
                            'text-sm font-medium text-gray-700',
                            props.disable
                                ? 'bg-gray-100 hover:bg-gray-200 cursor-not-allowed'
                                : 'hover:bg-gray-50 '
                        )}
                        onClickCapture={() => {
                            openMenu(open)
                        }}
                    >
                        <i className="feather-map-pin text-sm mr-2" />
                        <span
                            title={
                                is_title_label_overflowed ? getTitle() : ''
                            }
                            ref={title_label_ref}
                            className="truncate max-w-[125px] block"
                            onMouseOver={(event) =>
                                tooltip_ref.current &&
                                tooltip_ref.current.handleMouseOver(event)
                            }
                            onMouseOut={(event) =>
                                tooltip_ref.current &&
                                tooltip_ref.current.handleMouseOut(event)
                            }
                        >
                            {getTitle()}
                        </span>
                        {is_title_label_overflowed && (
                            <Tooltip
                                ref={tooltip_ref}
                                anchorElement="target"
                                position="top"
                            ></Tooltip>
                        )}

                        <ChevronDownIcon
                            className="-mr-1 ml-2 h-5 w-5 text-custom-dark-cyan-blue"
                            aria-hidden="true"
                        />
                    </Menu.Button>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                        show={open}
                    >
                        <Menu.Items
                            static
                            className={classNames(
                                props.position,
                                'absolute mt-2 w-56',
                                'rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5',
                                'focus:outline-none'
                            )}
                        >
                            <div className="py-1">
                                <Menu.Item key={'all_locations'}>
                                    {({ active }) => (
                                        <div
                                            className={classNames(
                                                active
                                                    ? 'bg-gray-100 text-gray-900'
                                                    : 'text-gray-700',
                                                'block text-sm',
                                                'mb-2'
                                            )}
                                        >
                                            <div className="mt-1 ml-2">
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        className="form-radio h-6 w-6"
                                                        name="radio-sizes"
                                                        value={'all'}
                                                        defaultChecked={
                                                            is_all_selected
                                                        }
                                                        onChange={
                                                            selectAllHandler
                                                        }
                                                    />
                                                    <Translation 
                                                        render_as = "span"
                                                        className ="ml-4 text-base"
                                                        content_key = "all_locations"
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </Menu.Item>
                                <hr className="mb-2" />
                                {props.items && props.items.length
                                    ? props.items.map(
                                          (
                                              i: Record<string, string>,
                                              idx
                                          ) => {
                                              return (
                                                  <Menu.Item key={i.id}>
                                                      {({ active }) => (
                                                          <div
                                                              className={classNames(
                                                                  active
                                                                      ? 'bg-gray-100 text-gray-900'
                                                                      : 'text-gray-700',
                                                                  'block text-sm',
                                                                  'mb-2'
                                                              )}
                                                          >
                                                              <div className="mt-1 ml-2">
                                                                  <label className="inline-flex items-center">
                                                                      <input
                                                                          type="radio"
                                                                          className="form-radio h-6 w-6"
                                                                          name="radio-sizes"
                                                                          value={
                                                                              i.id
                                                                          }
                                                                          defaultChecked={
                                                                              is_all_selected
                                                                                  ? false
                                                                                  : getValue(
                                                                                        idx
                                                                                    )
                                                                          }
                                                                          onChange={(
                                                                              e
                                                                          ) =>
                                                                              selectLocationLandler(
                                                                                  i
                                                                              )
                                                                          }
                                                                      />
                                                                      <span className="ml-4 text-base">
                                                                          {
                                                                              i.text
                                                                          }
                                                                      </span>
                                                                  </label>
                                                              </div>
                                                          </div>
                                                      )}
                                                  </Menu.Item>
                                              )
                                          }
                                      )
                                    : ''}
                            </div>
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    )
}

export default LocationSelector
